import { useAtomValue } from 'jotai'
import {
	storyAdMetaAttributionPositionAtom,
	storyAdMetaGeneralContentFontFamilyOverrideAtom,
	storyAdMetaGeneralContentPaddingAtom,
	storyAdMetaLocationPositionAtom,
	storyAdMetaLogoPositionAtom,
	storyAdMetaStackOrientationAtom,
} from '../state'
import { ContentAttribution, ContentCta, ContentLocation, ContentLogo, ContentSubtitle, ContentTitle } from './content'

type StoryAdContentLayerProps = {
	isGeneratingText: boolean
	shouldFlashContent: boolean
}

export function StoryAdContentLayer({ isGeneratingText, shouldFlashContent }: StoryAdContentLayerProps) {
	const storyAdMetaLogoPosition = useAtomValue(storyAdMetaLogoPositionAtom)
	const storyAdMetaLocationPosition = useAtomValue(storyAdMetaLocationPositionAtom)
	const storyAdMetaAttributionPosition = useAtomValue(storyAdMetaAttributionPositionAtom)

	const generalContentPadding = useAtomValue(storyAdMetaGeneralContentPaddingAtom)
	const fontFamilyOverride = useAtomValue(storyAdMetaGeneralContentFontFamilyOverrideAtom)

	const stackOrientation = useAtomValue(storyAdMetaStackOrientationAtom)

	if (generalContentPadding === undefined) {
		return null
	}

	// Instagram Stories safe zones: Top 15% (~266px on 1777px) covered by UI
	const topSafeZoneHeight = 135
	const bottomSafeZoneHeight = 120

	const topLeftZone = (
		<>
			{storyAdMetaLogoPosition === 'top-left' && <ContentLogo />}
			{storyAdMetaLocationPosition === 'top-left' && <ContentLocation isGeneratingText={isGeneratingText} />}
		</>
	)

	const topRightZone = (
		<>
			{storyAdMetaLogoPosition === 'top-right' && <ContentLogo />}
			{storyAdMetaLocationPosition === 'top-right' && <ContentLocation isGeneratingText={isGeneratingText} />}

			{storyAdMetaAttributionPosition === 'top-right' && <ContentAttribution />}
		</>
	)

	const bottomLeftZone = (
		<>
			{storyAdMetaLogoPosition === 'bottom-left' && <ContentLogo />}

			{storyAdMetaLocationPosition === 'bottom-left' && (
				<ContentLocation
					isGeneratingText={isGeneratingText}
				/>
			)}

			<ContentTitle isGeneratingText={isGeneratingText} />

			<ContentSubtitle isGeneratingText={isGeneratingText} />
		</>
	)

	const bottomRightZone = (
		<>
			{storyAdMetaLogoPosition === 'bottom-right' && <ContentLogo />}

			{storyAdMetaLocationPosition === 'bottom-right' && (
				<ContentLocation
					isGeneratingText={isGeneratingText}
				/>
			)}

			{storyAdMetaAttributionPosition === 'bottom-right' && <ContentAttribution />}
		</>
	)

	if (stackOrientation === 'horizontal') {
		return (
			<div
				className={`absolute inset-0 grid grid-cols-[50%_50%] ${shouldFlashContent ? 'animate-pulse' : ''}`}
				style={{
					fontFamily: fontFamilyOverride ?? 'inherit',
				}}
			>
				<div
					className="flex flex-col items-start"
					style={{
						paddingLeft: `${generalContentPadding}px`,
						paddingRight: `${generalContentPadding / 1.5}px`,
						paddingTop: `${generalContentPadding}px`,
					}}
				>
					<div style={{ height: `${topSafeZoneHeight}px` }} />

					<div className="flex items-center flex-col gap-2" data-is="top-left zone">
						{topLeftZone}
					</div>

					<div className="grow" />

					<div
						className="relative flex justify-end flex-col gap-4 z-50"
						style={{
							paddingBottom: `${generalContentPadding * 1.8}px`,
						}}
					>
						{bottomLeftZone}

						<ContentCta isGeneratingText={isGeneratingText} />
					</div>
				</div>

				<div className="flex flex-col items-end" style={{ marginBottom: `${bottomSafeZoneHeight}px` }}>
					<div style={{ height: `${topSafeZoneHeight}px` }} />

					<div
						style={{
							paddingRight: `${generalContentPadding}px`,
							paddingTop: `${generalContentPadding}px`,
						}}
						data-is="top-right zone"
						className="flex flex-col gap-2 items-center"
					>
						{topRightZone}
					</div>

					<div className="grow-1" />

					<div
						className="flex justify-between items-end pt-6"
						style={{
							paddingRight: `${generalContentPadding}px`,
							paddingBottom: `${generalContentPadding}px`,
						}}
					>
						<div data-is="bottom-right zone" className="flex flex-col gap-2 items-end">
							{bottomRightZone}
						</div>
					</div>
				</div>
			</div>
		)
	} else {
		return (
			<div
				className={`absolute inset-0 flex flex-col ${shouldFlashContent ? 'animate-pulse' : ''}`}
				style={{
					paddingTop: `${generalContentPadding}px`,
					fontFamily: fontFamilyOverride ?? 'inherit',
				}}
			>
				<div style={{ height: `${topSafeZoneHeight}px` }} />

				<div
					className="flex"
					style={{
						paddingLeft: `${generalContentPadding}px`,
						paddingRight: `${generalContentPadding}px`,
					}}
				>
					<div className="flex items-center flex-col gap-2" data-is="top-left zone">
						{topLeftZone}
					</div>

					<div className="grow-1" />

					<div data-is="top-right zone" className="flex flex-col gap-2 items-center">
						{topRightZone}
					</div>
				</div>

				<div className="grow-1" />

				<div
					className="relative flex justify-end flex-col gap-4 z-50"
					style={{
						paddingTop: `${Math.floor(generalContentPadding / 1.5)}px`,
						paddingLeft: `${generalContentPadding}px`,
						paddingRight: `${generalContentPadding}px`,
					}}
				>
					{bottomLeftZone}
				</div>

				<div
					className="flex justify-between items-end pt-6"
					style={{
						paddingLeft: `${generalContentPadding}px`,
						paddingRight: `${generalContentPadding}px`,
						paddingBottom: `${generalContentPadding * 1.8}px`,
						marginBottom: `${bottomSafeZoneHeight}px`,
					}}
				>
					<ContentCta isGeneratingText={isGeneratingText} />

					<div data-is="bottom-right zone" className="flex flex-col gap-2 items-end">
						{bottomRightZone}
					</div>
				</div>
			</div>
		)
	}
}
